import React from "react";
import {
  Modal,
  Button,
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Form,
  FormControl,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import classes from "./MultipleTestsModal.module.css";
import {
  renderTooltipOverlay,
  testDenominationFields,
  getSpeciesSearchByID,
  getCheckInputQuery,
  isAdvanced,
  API_URLS,
} from "../../shared/utility";
import { ReactComponent as InfoCircleLightIcon } from "../../assets/infoCircleLight.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import graphQLClientInstance from "../../axios-graphql-client";
import axios from "axios";

const MultipleTestsModal = (props: any) => {
  const { t } = useTranslation();

  const batchNameRef = React.createRef<HTMLInputElement>();
  const denominationsListRef = React.createRef<HTMLTextAreaElement>();
  const requestAdviceRef = React.createRef<HTMLInputElement>();

  const [previewTests, setPreviewTests] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [parsedDenominationsList, setParsedDenominationsList] = React.useState<any>([]);
  const [undefinedSpeciesIds, setUndefinedSpeciesIds] = React.useState<any>([]);

  React.useEffect(() => {
    setUndefinedSpeciesIds(undefinedAndUnique(parsedDenominationsList));
  }, [parsedDenominationsList])

  const isAdvancedUser = isAdvanced();

  const removeTest = (index: number) => {
    const listCopy = Array.from(parsedDenominationsList);
    listCopy.splice(index, 1);
    setParsedDenominationsList(listCopy);
  };

  const runTests = () => {
    const batchName = batchNameRef.current?.value;
    const requestAdvice = requestAdviceRef.current?.checked ? "1" : "0";
    const denominations: any[] = [];
    parsedDenominationsList.forEach((item: any) => {
      denominations.push({
        ...item,
        [testDenominationFields.BATCH_NAME]: batchName,
        [testDenominationFields.REQUEST_ADVICE]: requestAdvice,
      });
    });
    props.submitTests(denominations);
    props.setShow(false);
  };

  const addMultipleTests = () => {
    setParsedDenominationsList([]);
    if (denominationsListRef.current?.value) {
      const lines = denominationsListRef.current.value
        .split("\n")
        .filter(Boolean);
      const customDelimiter = '|-|-|-|-|-|';
      const denominationsList = lines
        .map((denomination) => {
          let maxOccurences = 4;
          return denomination.replace(/[;:,\t]+/g, s => maxOccurences ? (maxOccurences--, customDelimiter) : s).split(customDelimiter)})
        .filter(Boolean);
      const denominationsListNew: any[] = [];
      const denominationTexts = Array.from(
        new Set(denominationsList.map((denomination) => denomination[0]))
      );
      const speciesIds = Array.from(
        new Set(denominationsList.map((denomination) => denomination[1]))
      );
      setIsLoading(true);
      let denominationTextsErrors: any = [];
      let speciesByID: any = {};

      axios
        .all([
          graphQLClientInstance
            .post(API_URLS.CHECK_INPUT, getCheckInputQuery(denominationTexts))
            .catch((err) => {
              console.log(err);
            }),
          graphQLClientInstance
            .post(
              API_URLS.GRAPHQL,
              getSpeciesSearchByID(speciesIds)
            )
            .catch((err) => {
              console.log(err);
            }),
        ])

        .then(
          axios.spread((resCheckInput, resSpeciesByID) => {
            denominationTextsErrors = denominationTextsErrorsBuilder(
              resCheckInput
            );
            speciesByID = speciesByIDBuilder(resSpeciesByID);
          })
        )
        .finally(() => {
          denominationsList.forEach((denomination) => {
            denominationsListNew.push({
              [testDenominationFields.DENOMINATION]: denomination[0],
              [testDenominationFields.SPECIES_LATIN_NAME]:
                speciesByID[denomination[1]],
              [testDenominationFields.SPECIES_ID]: denomination[1],
              [testDenominationFields.BREEDERS_REFERENCE]: denomination[2],
              [testDenominationFields.BREEDERS_NAME]: denomination[3],
              [testDenominationFields.ADDITIONAL_INFORMATION]: denomination[4],
              error:
                denominationTextsErrors.includes(denomination[0]) ||
                !denomination[1] ||
                !speciesByID[denomination[1]],
            });
          });
          setParsedDenominationsList(denominationsListNew);
          setIsLoading(false);
        });
    }
    setPreviewTests(true);
  };

  return (
    <Modal
      centered
      show={props.show}
      className={classes.multipleTestsModal}
      onExited={() => {
        setParsedDenominationsList([]);
        setPreviewTests(false);
      }}
      onHide={() => props.setShow(false)}
    >
      <Modal.Header className={classes.header}>
        <Modal.Title>{t("submit_batch")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={classes.body}>
        <Container>
          <Row style={{ display: previewTests ? "none" : "block" }}>
            <Col md="6" id="multipleTestBatchNameContainer">
              <Form.Group controlId="multipleTest.batchName">
                <Form.Label className="w-100">
                  {t("batch_name")}
                  {renderTooltipOverlay(
                    t("tooltips_multiple_tests_modal.batch_name"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <FormControl
                  name="batchName"
                  as="input"
                  type="text"
                  ref={batchNameRef as React.RefObject<any>}
                />
              </Form.Group>
            </Col>
            <Col md="12" id="denominationsListContainer">
              <Form.Group controlId="multipleTest.denominationsList">
                <Form.Label className="w-100">
                  {t("multiple_test_denominations_list")}
                  {renderTooltipOverlay(
                    t("tooltips_multiple_tests_modal.multiple_test_denominations_list"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  name="denominationsList"
                  style={{ resize: "none" }}
                  placeholder={t("denominations_list_placeholder")}
                  ref={denominationsListRef as React.RefObject<any>}
                />
              </Form.Group>
            </Col>
            {isAdvancedUser ? (
              <Col md="12" id="multipleTestRequestAdviceContainer">
                <Form.Group controlId="multipleTest.requestAdvice">
                  <Form.Check
                    type="checkbox"
                    name="requestAdvice"
                    label={t("request_advice")}
                    ref={requestAdviceRef as React.RefObject<any>}
                  />
                </Form.Group>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row style={{ display: previewTests ? "block" : "none" }}>
            <Col md="12">
              <div className="pl-0">
                <FontAwesomeIcon
                  icon={["fas", "info-circle"]}
                  className={classes.infoCircle}
                />{" "}
                {t("multiple_tests_tip")}
              </div>
            </Col>
            <Col md="12" id="denominationsListContainer">
              <div
                className={[
                  "d-flex justify-content-center align-items-center",
                  classes.table,
                ].join(" ")}
              >
                {isLoading ? (
                  <Spinner animation="border" variant="primary" />
                ) : (
                  <Table striped hover size="sm" className={classes.tableFixed}>
                    <thead>
                      <tr>
                        <th>{t("denomination")}</th>
                        <th>{t("species_latin_name")}</th>
                        <th>{t("species_code")}</th>
                        <th>{t("breeders_reference")}</th>
                        <th>{t("breeders_name")}</th>
                        <th>{t("remark")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedDenominationsList.map(
                        (denomination: any, key: number) => {
                          return (
                            <tr
                              key={`parsed-denomination-${key}`}
                              className={
                                denomination["error"] ? classes.testError : ""
                              }
                            >
                              <td>
                                {
                                  denomination[
                                    testDenominationFields.DENOMINATION
                                  ]
                                }
                              </td>
                              <td>
                                {
                                  denomination[
                                    testDenominationFields.SPECIES_LATIN_NAME
                                  ]
                                }
                              </td>
                              <td>
                                {
                                  denomination[
                                    testDenominationFields.SPECIES_ID
                                  ]
                                }
                              </td>
                              <td>
                                {
                                  denomination[
                                    testDenominationFields.BREEDERS_REFERENCE
                                  ]
                                }
                              </td>
                              <td>
                                {
                                  denomination[
                                    testDenominationFields.BREEDERS_NAME
                                  ]
                                }
                              </td>
                              <td style={{wordWrap: 'break-word'}}>
                                {
                                  denomination[
                                    testDenominationFields
                                      .ADDITIONAL_INFORMATION
                                  ]
                                }
                              </td>
                              <td>
                                <FontAwesomeIcon
                                  onClick={() => removeTest(key)}
                                  icon={["fas", "trash-alt"]}
                                  className={["align-middle", classes.trashIcon].join(" ")}
                                  style={{ margin: "0 12.5px", cursor: 'pointer' }}
                                  color="#808080"
                                />
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Row className={["w-100", classes.actionsRow].join(" ")}>
          <Col md="7">
            {parsedDenominationsList.filter(
              (denomination: any) => denomination.error
            ).length > 0 ? (
              <Row>
                <Col md="2" style={{ maxWidth: "40px" }}>
                  <FontAwesomeIcon
                    icon={["fas", "info-circle"]}
                    className={[classes.infoCircle, classes.red].join(" ")}
                  />
                </Col>
                <Col md="10" className={classes.testDenominationError}>
                  {t("test_denomination_error")}
                </Col>
              </Row>
            ) : (
              <></>
            )}
          </Col>
          <Col md="5">
            {!previewTests ? (
              <Button
                id="add"
                variant="primary"
                onClick={() => addMultipleTests()}
                type="submit"
                className="float-right"
              >
                {t("add")}
              </Button>
            ) : (
              <></>
            )}
            {previewTests ? (
              !isLoading &&
              parsedDenominationsList &&
              parsedDenominationsList.filter(
                (denomination: any) => !denomination.error
              ).length &&
              parsedDenominationsList.filter(
                (denomination: any) => denomination.error
              ).length === 0 ? (
                <Button
                  id="runMultipleTests"
                  variant="primary"
                  onClick={() => runTests()}
                  type="submit"
                  className="float-right"
                >
                  {t("run_test")}
                </Button>
              ) : (
                <></>
              )
            ) : (
              <></>
            )}
            {previewTests? (
              <Button
                id="back"
                variant="link"
                onClick={() => {
                  setParsedDenominationsList([]);
                  setPreviewTests(false);
                }}
                type="button"
                className="float-right mr-2"
              >
                {t("back")}
              </Button>
            ) : (
              <Button
                id="cancelMultipleTests"
                variant="link"
                onClick={() => props.setShow(false)}
                className="float-right mr-2"
              >
                {t("cancel")}
              </Button>
            )}
          </Col>
          {undefinedSpeciesIds.length > 0 ? (
            <>
              <Col md="2" style={{ maxWidth: "40px" }}>
                <FontAwesomeIcon
                  icon={["fas", "info-circle"]}
                  className={[classes.infoCircle, classes.red].join(" ")}
                />
              </Col>
              <Col md="10" className={classes.testDenominationError}>
                {t("invalid_species_code", {count: undefinedSpeciesIds.length})}
                {': '}{undefinedSpeciesIds.join(", ")}
              </Col>
            </>
          ) : (
            <></>
          )}
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

const denominationTextsErrorsBuilder = (resCheckInput: any) => {
  if (
    resCheckInput?.data?.response === "ERROR" &&
    resCheckInput?.data?.error_list
  ) {
    return resCheckInput.data.error_list;
  } else {
    return [];
  }
};

const speciesByIDBuilder = (resSpeciesByID: any) => {
  const speciesByID: any = {};
  if (
    resSpeciesByID?.data?.data?.speciesSearchByID?.length
  ) {
    resSpeciesByID.data.data.speciesSearchByID.forEach((data: any) => {
      speciesByID[data.species_id] = data.princ_bot;
    });
  }
  return speciesByID
};

export default MultipleTestsModal;

function onlyUnique(value: any, index: any, self: string | any[]) {
  return self.indexOf(value) === index;
}

const undefinedAndUnique = (parsedDenominations: any) => {
  const undefinedSpecies: string[] = parsedDenominations
    .filter((denomination: any) => denomination.species_id && denomination.species_latin_name === undefined)
    .map((item: { species_id: any; }) => item.species_id?.trim())
  
  return undefinedSpecies.filter(onlyUnique);
}

